import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  AppBar,
  Toolbar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tab,
  Tabs,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Preview as PreviewIcon,
  Code as CodeIcon
} from '@mui/icons-material';
import PuckEditor from '../../components/MicroSite/PuckEditor';
import PageEditor from '../../components/Editor/PageEditor';
import { debugApiResponse } from '../../utils/apiUtils';

// Mock data for development
const MOCK_PAGE = {
  id: 'mock-page-1',
  title: 'Demo Page',
  slug: 'demo-page',
  content: JSON.stringify({
    content: [
      {
        type: 'Hero',
        props: {
          title: 'Welcome to the Page Builder',
          subtitle: 'This is a demo page created with mock data',
          backgroundImage: 'https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',
          buttonText: 'Learn More',
          buttonLink: '#',
          overlayColor: 'rgba(0, 0, 0, 0.5)',
          textColor: '#ffffff',
          height: '500px',
          align: 'center'
        }
      },
      {
        type: 'TextBlock',
        props: {
          heading: 'About the Page Builder',
          content: '<p>This is a demo of the Page Builder. You can drag and drop components to create beautiful pages without writing code.</p>',
          backgroundColor: '#ffffff',
          textColor: '#333333',
          padding: '2rem',
          align: 'left',
          maxWidth: '800px'
        }
      }
    ],
    root: {
      props: {
        style: {
          backgroundColor: "#ffffff"
        }
      }
    }
  }),
  metaDescription: 'A demo page for the Page Builder',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};

const PageBuilder = () => {
  const { accountId, microSiteId, pageId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(null);
  const [pageContent, setPageContent] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [editorMode, setEditorMode] = useState('visual'); // 'visual' or 'html'
  const [htmlContent, setHtmlContent] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [pageSettings, setPageSettings] = useState({
    title: '',
    slug: '',
    metaDescription: ''
  });
  const [useMockData, setUseMockData] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Fetch page data
  useEffect(() => {
    const fetchPageData = async () => {
      setLoading(true);
      setError(null);
      setUseMockData(false);
      
      try {
        // Check if we should use mock data
        if (pageId.startsWith('mock-page-') || !accountId) {
          console.log('Using mock data for page:', pageId);
          // Use mock data
          setPageContent(JSON.parse(MOCK_PAGE.content));
          setPageSettings({
            title: MOCK_PAGE.title,
            slug: MOCK_PAGE.slug,
            metaDescription: MOCK_PAGE.metaDescription
          });
          setLoading(false);
          return;
        }
        
        const apiUrl = `/api/accounts/${accountId}/microsites/${microSiteId}/pages/${pageId}`;
        console.log('Attempting to fetch page data from:', apiUrl);
        
        // Try a direct fetch with no-cache headers to avoid 304 responses
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Accept': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        
        console.log('API response status:', response.status);
        console.log('API response status text:', response.statusText);
        
        // Check for 304 Not Modified specifically
        if (response.status === 304) {
          console.warn('Received 304 Not Modified. This usually means the endpoint exists but the content hasn\'t changed.');
          console.warn('However, since we\'re having issues, we\'ll fall back to mock data.');
          setUseMockData(true);
          setPageContent(JSON.parse(MOCK_PAGE.content));
          setPageSettings({
            title: 'New Page (API returned 304)',
            slug: 'new-page',
            metaDescription: 'This is a new page'
          });
          setError('API returned 304 Not Modified. Using mock data instead. The API endpoint may not be properly implemented.');
          setLoading(false);
          return;
        }
        
        // Handle different response statuses
        if (response.status === 404) {
          // Page doesn't exist yet, use mock data
          console.log('Page not found (404), using mock data');
          setUseMockData(true);
          setPageContent(JSON.parse(MOCK_PAGE.content));
          setPageSettings({
            title: 'New Page',
            slug: 'new-page',
            metaDescription: 'This is a new page'
          });
          setError('The requested page does not exist. Using mock data instead. You can save this page to create it.');
        } else if (response.status === 401 || response.status === 403) {
          // Authentication or authorization error
          const responseText = await response.text();
          console.error('Authentication error. Response:', responseText);
          setUseMockData(true);
          setPageContent(JSON.parse(MOCK_PAGE.content));
          setError('Authentication error: You do not have permission to access this page. Using mock data instead.');
        } else if (!response.ok) {
          // Other error
          const responseText = await response.text();
          console.error('API error. Response:', responseText);
          
          // Try to determine if it's HTML or JSON
          if (responseText.trim().startsWith('<')) {
            console.error('Received HTML response instead of JSON. Server may be returning an error page.');
            console.error('HTML response (first 500 chars):', responseText.substring(0, 500));
            setError('Server error: Received HTML instead of JSON. The API endpoint may not exist or be misconfigured.');
          } else {
            try {
              const errorData = JSON.parse(responseText);
              console.error('API error data:', errorData);
              setError(`Failed to load page data: ${errorData.error || 'Unknown error'}`);
            } catch (e) {
              setError('Failed to load page data: Server returned an invalid response');
            }
          }
          
          // Fall back to mock data
          setUseMockData(true);
          setPageContent(JSON.parse(MOCK_PAGE.content));
          setPageSettings({
            title: 'Error Page',
            slug: 'error-page',
            metaDescription: 'This page loaded with errors'
          });
        } else {
          // Success - but we need to check if the response is actually JSON
          let responseText;
          try {
            responseText = await response.text();
            
            // Check if the response starts with HTML
            if (responseText.trim().startsWith('<')) {
              console.error('Received HTML response instead of JSON even though status is OK');
              console.error('HTML response (first 500 chars):', responseText.substring(0, 500));
              throw new Error('Received HTML instead of JSON');
            }
            
            // Try to parse as JSON
            const data = JSON.parse(responseText);
            console.log('Fetched page data:', data);
            
            // Set page settings
            setPageSettings({
              title: data.title || 'Untitled Page',
              slug: data.slug || 'untitled-page',
              metaDescription: data.metaDescription || ''
            });
            
            // Check if this is HTML content type
            if (data.contentType === 'html') {
              console.log('Page has HTML content type, switching to HTML editor mode');
              setEditorMode('html');
              setHtmlContent(data.content || '');
              
              // Also set a dummy Puck content for visual mode fallback
              setPageContent({
                content: [
                  {
                    type: 'TextBlock',
                    props: {
                      heading: data.title || 'HTML Content',
                      content: '<p>This page has HTML content and is best edited in HTML mode.</p>',
                      backgroundColor: '#ffffff',
                      textColor: '#333333',
                      padding: 'medium',
                      align: 'left',
                      maxWidth: 'large'
                    }
                  }
                ],
                root: {
                  props: {
                    style: {
                      backgroundColor: "#ffffff"
                    }
                  }
                }
              });
            } else {
              // Handle Puck content
              if (data.content) {
                let processedContent;
                
                // Check if content is already a JSON object
                if (typeof data.content === 'object') {
                  processedContent = data.content;
                  console.log('Content is already a JSON object');
                } 
                // Check if content is a JSON string
                else if (typeof data.content === 'string') {
                  try {
                    // Try to parse as JSON
                    processedContent = JSON.parse(data.content);
                    console.log('Successfully parsed content as JSON');
                  } catch (e) {
                    console.log('Content is not valid JSON, treating as HTML/text');
                    
                    // If it's HTML or plain text, convert it to a Puck-compatible format
                    // with a TextBlock component containing the HTML
                    processedContent = {
                      content: [
                        {
                          type: 'TextBlock',
                          props: {
                            heading: '',
                            content: data.content,
                            backgroundColor: '#ffffff',
                            textColor: '#333333',
                            padding: '2rem',
                            align: 'left',
                            maxWidth: '800px'
                          }
                        }
                      ],
                      root: {
                        props: {
                          style: {
                            backgroundColor: "#ffffff"
                          }
                        }
                      }
                    };
                    console.log('Converted HTML/text to Puck format:', processedContent);
                    
                    // Also set the HTML content in case we switch to HTML mode
                    setHtmlContent(data.content);
                  }
                } else {
                  console.warn('Content is neither an object nor a string, using mock data');
                  processedContent = JSON.parse(MOCK_PAGE.content);
                  setUseMockData(true);
                }
                
                setPageContent(processedContent);
              } else {
                console.warn('No content in page data, using mock data');
                setPageContent(JSON.parse(MOCK_PAGE.content));
                setUseMockData(true);
              }
            }
          } catch (error) {
            console.error('Error processing response:', error);
            console.error('Raw response text:', responseText);
            setError('Failed to process server response. The server may have returned invalid data.');
            
            // Fall back to mock data
            setPageContent(JSON.parse(MOCK_PAGE.content));
            setPageSettings({
              title: 'Error Page',
              slug: 'error-page',
              metaDescription: 'This page loaded with errors'
            });
            setUseMockData(true);
          }
        }
      } catch (error) {
        console.error('Error fetching page data:', error);
        setError(`Failed to load page data: ${error.message}. The API endpoint may not exist in server.js.`);
        // Fall back to mock data
        setPageContent(JSON.parse(MOCK_PAGE.content));
        setPageSettings({
          title: 'Error Page',
          slug: 'error-page',
          metaDescription: 'This page loaded with errors'
        });
        
        // Set useMockData flag to true
        setUseMockData(true);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPageData();
  }, [microSiteId, pageId, accountId]);

  // Modified handleContentChange to handle both visual and HTML modes
  const handleContentChange = (newContent) => {
    if (editorMode === 'visual') {
      setPageContent(newContent);
    } else {
      setHtmlContent(newContent);
    }
  };

  // Modified handleSave to save both visual and HTML content
  const handleSave = async () => {
    try {
      setSnackbar({
        open: true,
        message: 'Saving page...',
        severity: 'info'
      });
      
      // Use mock data path if applicable
      if (useMockData || !accountId) {
        console.log('Mock data mode - pretending to save page');
        setSnackbar({
          open: true,
          message: 'Page saved (mock mode)',
          severity: 'success'
        });
        return;
      }
      
      const apiUrl = `/api/accounts/${accountId}/microsites/${microSiteId}/pages/${pageId}`;
      
      const payload = {
        title: pageSettings.title,
        slug: pageSettings.slug,
        metaDescription: pageSettings.metaDescription,
        contentType: editorMode === 'visual' ? 'puck' : 'html',
        content: editorMode === 'visual' ? JSON.stringify(pageContent) : htmlContent
      };
      
      console.log('Saving page with payload:', payload);
      
      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error saving page:', errorText);
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.error || 'Failed to save page');
        } catch (e) {
          throw new Error('Failed to save page: ' + response.status);
        }
      }
      
      const data = await response.json();
      console.log('Save successful:', data);
      
      setSnackbar({
        open: true,
        message: 'Page saved successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error in handleSave:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to save page',
        severity: 'error'
      });
    }
  };

  // Handle settings change
  const handleSettingsChange = (e) => {
    const { name, value } = e.target;
    setPageSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle settings save
  const handleSettingsSave = () => {
    setSettingsDialog(false);
    // The actual save happens in handleSave
  };

  // Handle publish
  const handlePublish = async () => {
    // If we're using mock data, just simulate a successful publish
    if (useMockData || !accountId) {
      setSnackbar({
        open: true,
        message: 'Page published successfully! (Demo Mode - API endpoint not available)',
        severity: 'warning'
      });
      return;
    }
    
    setPublishing(true);
    
    try {
      // First save the page to ensure all changes are saved
      await handleSave();
      
      // Then publish the page
      const apiUrl = `/api/accounts/${accountId}/microsites/${microSiteId}/pages/${pageId}/publish`;
      console.log('Attempting to publish page:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Accept': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        body: JSON.stringify({
          isPublished: true,
          publishedAt: new Date().toISOString()
        })
      });
      
      console.log('Publish API response status:', response.status);
      
      // If the publish endpoint doesn't exist, fall back to the regular save endpoint
      if (response.status === 404) {
        console.warn('Publish endpoint not found, falling back to regular save endpoint');
        
        const saveResponse = await fetch(`/api/accounts/${accountId}/microsites/${microSiteId}/pages/${pageId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Accept': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
          body: JSON.stringify({
            content: typeof pageContent === 'string' ? pageContent : JSON.stringify(pageContent),
            title: pageSettings.title,
            slug: pageSettings.slug,
            metaDescription: pageSettings.metaDescription,
            isPublished: true,
            publishedAt: new Date().toISOString()
          })
        });
        
        if (saveResponse.ok) {
          setSnackbar({
            open: true,
            message: 'Page published successfully!',
            severity: 'success'
          });
        } else {
          throw new Error(`Server returned error: ${saveResponse.status} ${saveResponse.statusText}`);
        }
      } else if (response.ok) {
        setSnackbar({
          open: true,
          message: 'Page published successfully!',
          severity: 'success'
        });
      } else {
        throw new Error(`Server returned error: ${response.status} ${response.statusText}`);
      }
    } catch (err) {
      console.error('Error publishing page:', err);
      setSnackbar({
        open: true,
        message: `Error publishing page: ${err.message}`,
        severity: 'error'
      });
    } finally {
      setPublishing(false);
    }
  };

  // Handle back button
  const handleBack = () => {
    if (accountId) {
      navigate(`/account/${accountId}/microsites/${microSiteId}/pages`);
    } else {
      navigate(`/account/microsites/${microSiteId}/pages`);
    }
  };

  // Render the editor based on the current mode
  const renderEditor = () => {
    if (previewMode) {
      // In preview mode, render the preview based on the editor mode
      return (
        <Box sx={{ height: 'calc(100vh - 64px)', overflow: 'auto', bgcolor: '#fff' }}>
          {editorMode === 'visual' ? (
            <iframe
              title="Preview"
              src={`/preview/${accountId}/${microSiteId}/${pageId}`}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          ) : (
            <Box sx={{ p: 2 }}>
              <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
            </Box>
          )}
        </Box>
      );
    }
    
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (error) {
      return (
        <Box sx={{ p: 4, border: '1px solid #f44336', borderRadius: 2, bgcolor: '#ffebee', m: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          {useMockData && pageContent && (
            <Alert severity="info">
              Using mock data. You can still modify and save this page.
            </Alert>
          )}
        </Box>
      );
    }
    
    if (editorMode === 'visual' && pageContent) {
      return (
        <Box sx={{ height: 'calc(100vh - 124px)' }}>
          <PuckEditor
            data={pageContent}
            onChange={handleContentChange}
            readOnly={false}
            accountId={accountId}
            microSiteId={microSiteId}
            pageId={pageId}
            onSave={handleSave}
          />
        </Box>
      );
    } else if (editorMode === 'html') {
      return (
        <Box sx={{ height: 'calc(100vh - 124px)' }}>
          <PageEditor
            initialContent={htmlContent}
            onChange={handleContentChange}
            accountId={accountId}
            microSiteId={microSiteId}
            pageId={pageId}
          />
        </Box>
      );
    }
    
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress />
      </Box>
    );
  };

  // Add ResizeObserver error handling for the entire page
  useEffect(() => {
    // Create a handler for ResizeObserver errors
    const handleResizeObserverError = (event) => {
      if (event && event.message && event.message.includes('ResizeObserver')) {
        // Prevent the error from being displayed in the console
        event.stopImmediatePropagation();
        event.preventDefault();
        return false;
      }
      return true;
    };
    
    // Add event listener for unhandled errors
    window.addEventListener('error', handleResizeObserverError, true);
    
    // Clean up
    return () => {
      window.removeEventListener('error', handleResizeObserverError, true);
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !pageContent) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button 
          variant="contained" 
          startIcon={<ArrowBackIcon />} 
          onClick={handleBack}
          sx={{ mt: 2 }}
        >
          Back to Microsite
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* App Bar */}
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {pageSettings.title || 'Untitled Page'}
          </Typography>
          
          {/* Editor Mode Toggle */}
          <Tabs
            value={editorMode}
            onChange={(e, newValue) => setEditorMode(newValue)}
            sx={{ mr: 2 }}
          >
            <Tab value="visual" label="Visual" icon={<EditIcon />} />
            <Tab value="html" label="HTML" icon={<CodeIcon />} />
          </Tabs>
          
          {/* Preview Toggle */}
          <IconButton
            color="inherit"
            onClick={() => setPreviewMode(!previewMode)}
            title={previewMode ? 'Exit Preview' : 'Preview'}
            sx={{ mr: 1 }}
          >
            {previewMode ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </IconButton>
          
          {/* Settings */}
          <IconButton
            color="inherit"
            onClick={() => setSettingsDialog(true)}
            title="Page Settings"
            sx={{ mr: 1 }}
          >
            <SettingsIcon />
          </IconButton>
          
          {/* Save Button */}
          <Button
            variant="contained"
            color="secondary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            sx={{ mr: 1 }}
          >
            Save
          </Button>
          
          {/* Publish Button */}
          <Button
            variant="contained"
            color="primary"
            startIcon={<PreviewIcon />}
            onClick={handlePublish}
            disabled={publishing}
          >
            {publishing ? 'Publishing...' : 'Publish'}
          </Button>
        </Toolbar>
      </AppBar>
      
      {/* Main Content Area */}
      {renderEditor()}
      
      {/* Page Settings Dialog */}
      <Dialog open={settingsDialog} onClose={() => setSettingsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Page Settings</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Page Title"
            name="title"
            value={pageSettings.title}
            onChange={handleSettingsChange}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Page Slug"
            name="slug"
            value={pageSettings.slug}
            onChange={handleSettingsChange}
            margin="normal"
            helperText="The URL-friendly version of the title"
          />
          <TextField
            fullWidth
            label="Meta Description"
            name="metaDescription"
            value={pageSettings.metaDescription}
            onChange={handleSettingsChange}
            margin="normal"
            multiline
            rows={3}
            helperText="A brief description of the page for search engines"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialog(false)}>Cancel</Button>
          <Button onClick={handleSettingsSave} color="primary" variant="contained">
            Apply
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PageBuilder; 